import React, { useRef } from "react";
import { Link } from "react-router-dom";

import { Calculator, Rocket } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import CTAButtonLink from "../CTA/CTAButtonLink.jsx";
import ScrollModal from "../ui/ScrollModal.jsx";

import Tooltip from "./Tooltip";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig.js";
import { OddsMonkeyFT } from "../Affiliate/Om.jsx";
import fbho from './images/fbhigherodds.png'
const FreeBetHighOddsGuide = () => {
  const meta = guidesConfig.freeBetHighOddsGuide?.seo || {};
  const scrollRef = useRef(null);

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.freeBetHighOddsGuide?.route || "/guides"}
      />

      <p>
        When matched betting, free bets are a key way to earn <strong>profit</strong>. But to get the most out of them, we need to understand <strong>how odds affect our return</strong>.
      </p>
      <p>
        One of the most important strategies is using free bets on <strong>higher odds</strong> — usually between 5.0 and 7.0. This guide explains why, with practical tips and examples to help you increase your profit from every free bet.
      </p>

      <h2>What Is a Free Bet and How Does It Work?</h2>
      <p>
        A <strong>free bet</strong> is a bookmaker offer that gives you a stake to bet with, without using your own money. You often get it for signing up or placing a <Tooltip content="A qualifying bet is a bet you place with your own money to unlock a free bet offer.">qualifying bet</Tooltip>.
      </p>
      <h6 className="info">
        If your free bet wins, you only get the <strong>winnings</strong>, not the <strong>stake</strong>. The stake belongs to the bookie and disappears after the bet is placed.
      </h6>
      <div className="example-container">
        <h4>example</h4>
        <p>
        £10 normal bet at odds of 3.0 = £20 profit + £10 stake = £30 total return
        </p>
        <p>
        £10 <strong>free bet</strong> at odds of 3.0 = <strong>£20 profit</strong> (stake not returned)
        </p>
      </div>
        <CTAButtonLink 
        to="/get-started/step1"
        text="Understand How Matched Betting Works in 180 Seconds"
        buttonText="Start now"
        icon={Rocket}
        tabText="Popular"
      />

      <h2>Why Higher Odds Increase Free Bet Profit</h2>
      <p>
        Since the stake isn’t returned, all your value comes from the <strong>winnings</strong>. That means the <strong>higher the odds</strong>, the <strong>more value</strong> you can extract from a free bet.
      </p>
      <h6 className="info">
        <strong>Example:</strong><br />
        £10 free bet at odds of 1.2 = £2 profit<br />
        £10 free bet at odds of 5.0 = £40 profit
      </h6>
      <p>
        Even after laying the bet on a <Tooltip content="A betting exchange lets you bet against an outcome, matching your bet with another user.">betting exchange</Tooltip>, you’ll lock in a much better profit with higher odds — typically around <strong>70–80%</strong> of the free bet’s face value.
      </p>

      <h2>What’s So Special About Odds Around 5.0?</h2>
      <p>Matched bettors often use odds between <strong>5.0 and 7.0</strong> when placing free bets. Here’s why:</p>
      <ul>
        <li>
        <strong>Strong returns:</strong> Odds of 5.0 return £40 profit from a £10 free bet, allowing you to lock in 80%.

        </li>
        <li>
        <strong>Manageable lay liability:</strong> Higher odds increase your <Tooltip content="Lay liability is the amount you stand to lose on the exchange if your lay bet fails.">lay liability</Tooltip>. At very high odds, this can tie up more of your bankroll.

        </li>
      </ul>
      <h6 className="info">
        <strong>Lay liability</strong> is the amount you have to pay to your backer if the lay bet fails. It increases with higher odds.
      </h6>
        <pre>
          <code>Liability = Stake * (Lay Odds - 1)</code>
        </pre>
      <p>Exchanges freeze the amount you'd have to pay out, to guarantee you can pay out if you lose.</p>
      <p>While it's tied up, you can't use that money to place other bets and make more profit.</p>
      <p><strong>Don't worry – if you win, you'll get it back; if you lose, you'll win your <span className="back">back</span> bet.</strong></p>
    <img src={fbho} alt="Why use free bets at higher odds" />
    <p>
  On the left, using low odds (2.0–4.0) doesn't lock in enough profit. On the far right, while profit increases slightly, <strong>lay liability also shoots up</strong>, meaning you need to tie up a lot more money on the exchange, which isn't great when you're starting out. The green middle zone — around 5.0 to 7.0 — offers the best balance: <strong>high enough returns without excessive liability</strong>.
</p>

      <p>
        Odds around 5.0–7.0 hit the sweet spot: <strong>high enough to get good profit</strong>, but <strong>not so high that you risk too much of your own money</strong>.
      </p>
      <h6 className="info">
          Use <a href="https://www.oddsmonkey.com/affliates/affiliate.php?id=66317" target="_blank" rel="noreferrer">OddsMonkey</a> to find the best matches available to maximise profit from your free bet, keep track of bets and profits, and find free bet offers. OddsMonkey helps you find matches that enable you to lock in more like 75-85% of a free bet.
        </h6>
      <div className="example-container">
      <h4>Example: £10 Free Bet at Odds of 7.0</h4>
      <h5>Step 1: Place your free bet</h5>
<p>
  Back a team at odds of <strong>7.0</strong> using your £10 free bet. If the bet wins, this returns <strong>£60</strong> — the stake isn’t returned.
</p>
<ScrollModal scrollContainerRef={scrollRef} props={{ctaKey: "om2"}} />

<h5>Step 2: Lay the same outcome on the exchange</h5>
<p>
  Lay <strong>£8.02</strong> on the team at odds of 7.5, with 2% commission. Your <Tooltip content="The amount you risk losing when placing a lay bet">liability</Tooltip> is <strong>£52.13</strong>.
</p>

      <p>
        Our <Link to="/calculators/free-bet-calculator">matched betting calculator</Link> tells us we'll lock in around <strong>£7.86 profit</strong> — that’s <strong>78.6%</strong> of the free bet’s value.
      </p>
      <ul>
        <li><strong>If your <span className="back">back</span> bet wins:</strong> You win £60 at the bookie and lose £52.13 on the exchange. Profit: £7.87</li>
        <li><strong>If your <span className="lay">lay</span> bet wins:</strong> You win £7.86 at the exchange (£8.02 - commission) and lose nothing at the bookie. Profit: £7.86</li>

      </ul>
      <CTALarge 
        to="/calculators/free-bet-calculator" 
        text="Go to Free Bet Calculator" 
        icon={Calculator} 
      />

</div>
 

      <h2>Tips to Maximise Profit from Free Bets</h2>
      <ul>
        <li>✅ Use odds between <strong>5.0 and 7.0</strong> to get the best balance between profit and lay liability</li>
        <li>✅ Look for <strong>close matches</strong> between back and lay odds</li>
        <li>✅ Use a <Link to="/calculators/free-bet-calculator">matched betting calculator</Link>to lock in maximum profit</li>
        <li>✅ Use <Tooltip content="A tool for finding close odds matches and tracking offers.">OddsMonkey</Tooltip> to find the best back-lay matches and track offers</li>
      </ul>
      <OddsMonkeyFT/>

      <h2>Final Thoughts</h2>
      <p>
        Free bets are one of the most valuable tools in matched betting. But if you want to make the most of them, you need to use them on <strong>higher odds</strong>.
      </p>
      <p>
        By sticking to odds around 5.0–7.0, you’ll:
      </p>
      <ul>
        <li>Get bigger returns from each free bet</li>
        <li>Lock in more profit (usually 70–85% of the free bet value)</li>
        <li>Keep your lay liability under control</li>
      </ul>
    </div>
  );
};

export default FreeBetHighOddsGuide;